import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { languageType, Message } from "utils/interfaces";
import ChatMessage from "./ChatMessage";

interface ChatMessagesParams {
  messages: Message[];
  showLoading: boolean;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  returnToIDE: () => void;
  moveToNextLesson: () => void;
  language: languageType | null;
  nameInitials: string;
}

const MULTIPLE_MESSAGES_TRANSITION = {
  duration: 0.5,
  ease: "linear",
};

const ChatMessages: React.FC<ChatMessagesParams> = ({
  messages,
  showLoading,
  AIAssistant,
  returnToIDE,
  moveToNextLesson,
  language,
  nameInitials,
}) => {
  // States definition
  const [width, setWidth] = useState(767);
  const [multipleMessages, setMultipleMessages] = useState(false);

  // References
  const widthRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMultipleMessages(messages && messages.length > 1);
  }, [messages.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      });
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [showLoading, setMultipleMessages, messages]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (widthRef.current) {
        const { width } = widthRef.current.getBoundingClientRect();
        if (width !== undefined && width !== 0) {
          setWidth(width);
        }
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      ref={scrollContainerRef}
      className="w-full h-full flex flex-col items-center overflow-y-auto custom-scroll overflow-x-hidden rounded-sm"
    >
      <div className="max-w-[767px] flex flex-col items-center relative">
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            {index === 0 ? (
              <div className="pb-6 flex">
                {/* text box*/}
                <div ref={widthRef} className="w-full h-auto relative">
                  <motion.div
                    key={`spacer-${index}`}
                    initial={{
                      height: multipleMessages ? 0 : 268 + 24,
                      paddingBottom: multipleMessages ? 0 : 24,
                    }}
                    animate={{
                      height: multipleMessages ? 0 : 268 + 24,
                      transition: MULTIPLE_MESSAGES_TRANSITION,
                      paddingBottom: multipleMessages ? 0 : 24,
                    }}
                  />
                  <motion.div
                    key={`message-${index}`}
                    className="w-fit p-4 bg-secondary-50 border border-secondary-300 h-fit-content rounded-tl-3xl rounded-bl-3xl rounded-br-3xl"
                    initial={{
                      marginLeft: 0,
                      marginRight: multipleMessages ? 84 : 0,
                    }}
                    animate={{
                      marginLeft: 0,
                      marginRight: multipleMessages ? 84 : 0,
                      borderTopRightRadius: multipleMessages ? 2 : 24,
                      transition: MULTIPLE_MESSAGES_TRANSITION,
                    }}
                  >
                    <div className="leading-10 gap-3 h-fit w-full">
                      <div className="self-stretch text-neutral-800 text-[23px] font-semibold leading-7">
                        {AIAssistant.greeting}
                      </div>
                      <div className="self-stretch text-neutral-800 text-base font-normal leading-7 my-3">
                        {AIAssistant.intro}
                      </div>
                      <div className="self-stretch text-neutral-800 text-base font-normal leading-7">
                        Pick an option below or enter your question to get
                        started.
                      </div>
                    </div>
                  </motion.div>
                </div>
                {/* image */}
                <motion.div
                  className="rounded-full flex items-center justify-center absolute mx-auto"
                  initial={{
                    width: 268,
                    height: 268,
                    right: (width - 268) / 2,
                    top: 24,
                  }}
                  animate={{
                    width: multipleMessages ? 74 : 268,
                    height: multipleMessages ? 74 : 268,
                    right: multipleMessages ? 0 : (width - 268) / 2,
                    top: multipleMessages ? 0 : 24,
                    transition: MULTIPLE_MESSAGES_TRANSITION,
                    padding: 0,
                  }}
                >
                  <motion.div
                    key={`background-${index}`}
                    className="w-full h-full rounded-full bg-secondary-50 border border-secondary-300 absolute"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: multipleMessages ? 1 : 0,
                      transition: MULTIPLE_MESSAGES_TRANSITION,
                    }}
                  />
                  <div className="w-full h-full relative overflow-hidden">
                    <img
                      key={`img-${index}`}
                      src={AIAssistant.img}
                      alt="AI Avatar"
                      className="w-full h-full"
                    />
                  </div>
                </motion.div>
              </div>
            ) : (
              // Actual chat message
              <ChatMessage
                index={index}
                AIAssistant={AIAssistant}
                message={message}
                returnToIDE={returnToIDE}
                moveToNextLesson={moveToNextLesson}
                language={language}
                nameInitials={nameInitials}
              />
            )}
          </React.Fragment>
        ))}
        {showLoading && (
          <ChatMessage
            index={messages.length}
            message={{ role: "assistant" }}
            AIAssistant={AIAssistant}
            language={language}
            nameInitials={nameInitials}
          />
        )}
      </div>
    </div>
  );
};

export default ChatMessages;
